import { PracticeTimezone } from "@elation/api-client/src/stitch-web/models/practiceTimezone";
import { HeaderText } from "@src/components/general/HeaderText";
import { Text } from "@src/components/general/Text";
import { VerticalFlexbox } from "@src/components/layout/Flexbox";
import { Wizard } from "@src/components/layout/Wizard";
import { LoginLayout } from "@src/pages/LoginLayout";
import { Formik } from "formik";
import * as yup from "yup";
import { AccountCreate } from "./CreateAccount.types";
import { AccountCredentialsStep } from "./steps/AccountCredentialsStep";
import { PersonalInfoStep } from "./steps/PersonalInfoStep";
import { PracticeInfoStep } from "./steps/PracticeInfoStep";

interface FormValues extends AccountCreate {
  credentialsQuery: string;
  stateQuery: string;
}

const validationSchema = yup.object<FormValues>({
  addressLine1: yup.string().required("Address is required"),
  addressLine2: yup.string().nullable(true),
  city: yup.string().required("City is required"),
  state: yup.string().required("State is required"),
  zip: yup.string().required("Zip code is required"),
  phone: yup.string().nullable(true),
  fax: yup.string().nullable(true),
  email: yup
    .string()
    .email("Invalid email address")
    .required("Email is required"),
  firstName: yup.string().required("First name is required"),
  middleName: yup.string(),
  lastName: yup.string().required("Last name is required"),
  specialty: yup.string().nullable(true),
  password: yup
    .string()
    .required("Password is required")
    .test(
      "no-whitespace",
      "Password cannot contain whitespace characters",
      (value) => (value ? !/\s/.test(value) : true),
    ),
  credentials: yup.string().nullable(true),
  npi: yup
    .string()
    .nullable(true)
    .matches(/^\d{10}$/, "NPI must be a 10 digit number"),
  timezone: yup
    .mixed()
    .oneOf(Object.values(PracticeTimezone), "Invalid timezone")
    .nullable(true),
  credentialsQuery: yup.string(),
  stateQuery: yup.string(),
});

export interface CreateAccountPageProps {
  onSubmit: (values: AccountCreate) => Promise<void>;
  error?: string | null;
}

export const CreateAccountPage = ({
  onSubmit,
  error = null,
}: CreateAccountPageProps) => {
  const defaultTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const handleSubmit = async (values: FormValues) => {
    const { credentialsQuery: _, stateQuery: __, ...restValues } = values;

    const cleanedValues: AccountCreate = {
      ...restValues,
      addressLine2:
        values.addressLine2 && values.addressLine2.trim()
          ? values.addressLine2
          : null,
      phone: values.phone && values.phone.trim() ? values.phone : null,
      fax: values.fax && values.fax.trim() ? values.fax : null,
      middleName:
        values.middleName && values.middleName.trim()
          ? values.middleName
          : null,
      credentials:
        values.credentials && values.credentials.trim()
          ? values.credentials
          : null,
      npi: values.npi && values.npi.trim() ? values.npi : null,
      timezone:
        values.timezone && values.timezone.trim()
          ? values.timezone
          : (defaultTimezone as PracticeTimezone),
    };

    await onSubmit(cleanedValues);
  };

  const initialValues: FormValues = {
    addressLine1: "",
    addressLine2: null,
    city: "",
    state: "",
    zip: "",
    phone: null,
    fax: null,
    email: "",
    firstName: "",
    middleName: "",
    lastName: "",
    password: "",
    credentials: null,
    npi: null,
    timezone: defaultTimezone as PracticeTimezone,
    credentialsQuery: "",
    specialty: null,
    stateQuery: "",
  };

  return (
    <LoginLayout>
      <Formik<FormValues>
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        validateOnChange
      >
        {({ submitForm, handleSubmit }) => (
          <VerticalFlexbox
            as="form"
            gap="md"
            method="post"
            onSubmit={handleSubmit}
            className="w-full"
          >
            <VerticalFlexbox gap="xs">
              <HeaderText tag="h1" className="text-3xl">
                Create Account
              </HeaderText>
              <Text sizing="sm" appearance="subdued">
                Set up your Stitch account in three quick steps.
              </Text>
            </VerticalFlexbox>

            {error ? (
              <Text
                appearance="danger"
                role="alert"
                sizing="sm"
                className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2"
              >
                {error || "An error occurred"}
              </Text>
            ) : null}

            <Wizard
              onFinish={() => {
                submitForm();
              }}
            >
              <PersonalInfoStep />
              <PracticeInfoStep />
              <AccountCredentialsStep />
            </Wizard>
          </VerticalFlexbox>
        )}
      </Formik>
    </LoginLayout>
  );
};
